import { NgTemplateOutlet } from '@angular/common';
import { computed, input, output, TemplateRef } from '@angular/core';
import { Marked } from 'marked';
import { AgentMessageActionsComponent } from 'AgentApp/ts/features/agent/components/agent-message-actions/agent-message-actions.component';
import { AGENT_MARKDOWN_RENDERER_OPTIONS, INCOMPLETE_MARKDOWN_LINK_REGEX, } from 'AgentApp/ts/constants/agent-markdown.constants';
import { AgentDownloadLinkComponent } from 'AgentApp/ts/features/agent/components/agent-download-link/agent-download-link.component';
import { AgentLinkInterceptDirective } from 'AgentApp/ts/features/agent/directives/agent-link-intercept.directive';
import * as i0 from "@angular/core";
const _c0 = a0 => ({ $implicit: a0 });
function AgentResponseMessageComponent_For_5_Conditional_0_Conditional_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "hr");
} }
function AgentResponseMessageComponent_For_5_Conditional_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵconditionalCreate(0, AgentResponseMessageComponent_For_5_Conditional_0_Conditional_0_Template, 1, 0, "hr");
    i0.ɵɵelementContainer(1, 5);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const action_r3 = ctx_r1.$implicit;
    const $index_r4 = ctx_r1.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵconditional($index_r4 === 0 ? 0 : -1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r4.chartActionTemplate())("ngTemplateOutletContext", i0.ɵɵpureFunction1(3, _c0, action_r3));
} }
function AgentResponseMessageComponent_For_5_Conditional_1_Conditional_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "hr");
} }
function AgentResponseMessageComponent_For_5_Conditional_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵconditionalCreate(0, AgentResponseMessageComponent_For_5_Conditional_1_Conditional_0_Template, 1, 0, "hr");
    i0.ɵɵelement(1, "ycn-agent-download-link", 6);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const action_r3 = ctx_r1.$implicit;
    const $index_r4 = ctx_r1.$index;
    i0.ɵɵconditional($index_r4 === 0 ? 0 : -1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("url", action_r3.display_result.url)("title", action_r3.display_result.title);
} }
function AgentResponseMessageComponent_For_5_Conditional_2_Conditional_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "hr");
} }
function AgentResponseMessageComponent_For_5_Conditional_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵconditionalCreate(0, AgentResponseMessageComponent_For_5_Conditional_2_Conditional_0_Template, 1, 0, "hr");
    i0.ɵɵelementStart(1, "div", 7)(2, "div", 8)(3, "a", 9);
    i0.ɵɵelement(4, "span", 10);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const action_r3 = ctx_r1.$implicit;
    const $index_r4 = ctx_r1.$index;
    i0.ɵɵconditional($index_r4 === 0 ? 0 : -1);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("href", action_r3.display_result.url, i0.ɵɵsanitizeUrl);
    i0.ɵɵadvance();
    i0.ɵɵproperty("textContent", action_r3.display_result.title);
} }
function AgentResponseMessageComponent_For_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵconditionalCreate(0, AgentResponseMessageComponent_For_5_Conditional_0_Template, 2, 5)(1, AgentResponseMessageComponent_For_5_Conditional_1_Template, 2, 3)(2, AgentResponseMessageComponent_For_5_Conditional_2_Template, 5, 3);
} if (rf & 2) {
    const action_r3 = ctx.$implicit;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ctx_r4.chartActionTemplate() && (action_r3.name === "create_fundamental_chart" || action_r3.name === "create_scatter_plot" || action_r3.name === "create_technical_chart") && (action_r3.display_result == null ? null : action_r3.display_result.state) ? 0 : (action_r3.display_result == null ? null : action_r3.display_result.type) === "download" ? 1 : action_r3.display_result ? 2 : -1);
} }
const marked = new Marked({ renderer: { ...AGENT_MARKDOWN_RENDERER_OPTIONS } });
/**
 * Agent Response Message
 *
 * Renders an assistant message — the markdown text plus any display actions — for both the agent page and the
 * AI side panel. The two surfaces differ only presentationally, controlled by inputs:
 *   - `chartActionTemplate`: a template the agent page supplies to render chart display actions as inline chart
 *     cards. The side panel supplies none, so chart actions fall through to a plain link — and, crucially, the
 *     heavy chart components are never imported here, keeping them out of the site-wide side-panel bundle.
 *   - `includeResponseProfileImage`: wraps the message in the agent page's white response box.
 *   - `displayAsCard`: wraps the message in a padded white card (side panel).
 *
 * A single message can contain more than one display action, so the dispatch lives here. Chart actions are
 * projected via `chartActionTemplate` (the caller owns the chart components); add new chart actions there.
 */
export class AgentResponseMessageComponent {
    constructor() {
        this.messageText = input.required(...(ngDevMode ? [{ debugName: "messageText" }] : []));
        this.includeResponseProfileImage = input(false, ...(ngDevMode ? [{ debugName: "includeResponseProfileImage" }] : []));
        this.chartActionTemplate = input(null, ...(ngDevMode ? [{ debugName: "chartActionTemplate" }] : []));
        this.displayAsCard = input(false, ...(ngDevMode ? [{ debugName: "displayAsCard" }] : []));
        this.displayActions = input([], ...(ngDevMode ? [{ debugName: "displayActions" }] : []));
        this.deleteChat = output();
        this.messageHtml = computed(() => {
            const cleanedContent = this.messageText().replace(INCOMPLETE_MARKDOWN_LINK_REGEX, '*Creating Link...*');
            return marked.parse(cleanedContent);
        }, ...(ngDevMode ? [{ debugName: "messageHtml" }] : []));
    }
}
AgentResponseMessageComponent.ɵfac = function AgentResponseMessageComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AgentResponseMessageComponent)(); };
AgentResponseMessageComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: AgentResponseMessageComponent, selectors: [["ycn-agent-response-message"]], inputs: { messageText: [1, "messageText"], includeResponseProfileImage: [1, "includeResponseProfileImage"], chartActionTemplate: [1, "chartActionTemplate"], displayAsCard: [1, "displayAsCard"], displayActions: [1, "displayActions"] }, outputs: { deleteChat: "deleteChat" }, decls: 7, vars: 10, consts: [["aiResponseElement", ""], ["ycnAgentLinkIntercept", ""], [1, "section-ai-response-content"], [1, "ai-md", 3, "innerHTML"], [3, "deleteChat", "contentElement"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [3, "url", "title"], [1, "section-ai-response-link", "mb-2"], [1, "d-flex"], ["target", "_blank", 1, "text-underline", "text-blue", 3, "href"], [3, "textContent"]], template: function AgentResponseMessageComponent_Template(rf, ctx) { if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelementStart(0, "div", 1)(1, "div", 2);
        i0.ɵɵelement(2, "div", 3, 0);
        i0.ɵɵrepeaterCreate(4, AgentResponseMessageComponent_For_5_Template, 3, 1, null, null, i0.ɵɵrepeaterTrackByIndex);
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(6, "ycn-agent-message-actions", 4);
        i0.ɵɵlistener("deleteChat", function AgentResponseMessageComponent_Template_ycn_agent_message_actions_deleteChat_6_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.deleteChat.emit()); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        const aiResponseElement_r6 = i0.ɵɵreference(3);
        i0.ɵɵclassMap(ctx.displayAsCard() ? "pt-12 pr-20 pb-12 pl-20 bg-white rounded-4" : "");
        i0.ɵɵclassProp("section-ai-response", ctx.includeResponseProfileImage());
        i0.ɵɵadvance();
        i0.ɵɵclassProp("pl-20", !ctx.displayAsCard())("pr-20", !ctx.displayAsCard());
        i0.ɵɵadvance();
        i0.ɵɵproperty("innerHTML", ctx.messageHtml(), i0.ɵɵsanitizeHtml);
        i0.ɵɵadvance(2);
        i0.ɵɵrepeater(ctx.displayActions());
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("contentElement", aiResponseElement_r6);
    } }, dependencies: [NgTemplateOutlet, AgentDownloadLinkComponent, AgentLinkInterceptDirective, AgentMessageActionsComponent], encapsulation: 2, changeDetection: 0 });
